import { Icon } from "./Icon";
import { formatPercent } from "../lib/format";
import type { ModuleProgress, ModuleSummary } from "../lib/types";

export type GateState = "done" | "active" | "todo" | "failed";

const GLYPH = {
  done: "check" as const,
  active: "circle-dot" as const,
  todo: "circle" as const,
  failed: "cross" as const,
};

const CLASS: Record<GateState, string> = {
  done: "mark mark-done",
  active: "mark mark-active",
  todo: "mark",
  failed: "mark mark-fail",
};

/** One of the three gates a module is made of, carrying its own state in colour and glyph. */
export function GateChip({ state, label }: { state: GateState; label: string }) {
  return (
    <span className={CLASS[state]}>
      <Icon name={GLYPH[state]} size={13} className="glyph" />
      {label}
    </span>
  );
}

export function moduleGates(
  module: ModuleSummary,
  progress: ModuleProgress | undefined,
): Array<{ key: string; state: GateState; label: string }> {
  const lessonsDone = progress?.lessonsDone ?? 0;
  const lessonsTotal = progress?.lessonsTotal ?? module.lessons.length;
  const gates: Array<{ key: string; state: GateState; label: string }> = [
    {
      key: "lessons",
      state: lessonsDone >= lessonsTotal && lessonsTotal > 0
        ? "done"
        : lessonsDone > 0 ? "active" : "todo",
      label: `Lessons ${lessonsDone}/${lessonsTotal}`,
    },
  ];

  if (module.quizId) {
    gates.push({
      key: "quiz",
      state: progress?.quizPassed
        ? "done"
        : progress?.quizBestScore !== null && progress?.quizBestScore !== undefined
          ? "failed"
          : "todo",
      label: progress?.quizPassed
        ? `Quiz ${formatPercent(progress.quizBestScore)}`
        : progress?.quizBestScore !== null && progress?.quizBestScore !== undefined
          ? `Quiz ${formatPercent(progress.quizBestScore)} — not passed`
          : "Quiz",
    });
  }

  if (module.labId) {
    const attempted = progress?.labScore !== null && progress?.labScore !== undefined;
    gates.push({
      key: "lab",
      state: progress?.labPassed ? "done" : attempted ? "active" : "todo",
      label: progress?.labPassed
        ? `Lab ${progress.labScore}/${progress.labMaxScore}`
        : attempted
          ? `Lab ${progress.labScore}/${progress.labMaxScore} — in progress`
          : "Lab",
    });
  }

  return gates;
}

/** A thin labelled meter. Used for overall progress and in the admin tables. */
export function ProgressBar({
  percent,
  label,
  done = false,
}: {
  percent: number;
  label: string;
  done?: boolean;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div
      className={done ? "meter meter-done" : "meter"}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      aria-label={label}
    >
      <span style={{ width: `${clamped}%` }} />
    </div>
  );
}
